import { ComponentType } from 'react';
import { LucideIcon, LayoutDashboard, Users, CreditCard, ArrowUpRight, MessageSquare, Shield, Wallet, Building2, Sparkles, AlertTriangle, Coins, ShieldAlert, Globe, UserCog, Megaphone, LayoutPanelTop, ShieldCheck, Store, Percent, DollarSign, WalletCards, Terminal, Banknote, Bell, Gift, ClipboardList } from 'lucide-react';
import Dashboard from '../components/pages/dashboard/index';
import UsersManagement from '../components/pages/users';
import UserDetailPage from '../components/pages/users/UserDetailPage';
import TransactionsManagement from '../components/pages/transactions';
import TransactionDetailPage from '../components/pages/transactions/TransactionDetailPage';
import WithdrawalManagement from '../components/pages/withdrawalRequest';
import TicketManagement from '../components/pages/tickets';
import AdminManagement from '../components/pages/admins';
import GameWalletsManagement from '../components/pages/gameWallets';
import GameWalletProviderDetail from '../components/pages/gameWallets/GameWalletProviderDetail';
import GameProvidersManagement from '../components/pages/gameProviders';
import ScheduleManagement from '../components/pages/vfxConfig/schedules';
import ScheduleDetail from '../components/pages/vfxConfig/schedules/ScheduleDetail';
import TransactionLimitsManagement from '../components/pages/transactionLimits';
import MessagingSettingsManagement from '../components/pages/messagingSettings';
import SignupBonusManagement from '../components/pages/signupBonus';
import ProviderFeesManagement from '../components/pages/providerFees';
import UsdWalletsManagement from '../components/pages/usdWallets';
import CreditWalletsManagement from '../components/pages/creditWallets';
import CashTransactionsManagement from '../components/pages/cashTransactions';
import CashTransactionDetailPage from '../components/pages/cashTransactions/CashTransactionDetailPage';
import ManualGameDepositsManagement from '../components/pages/manualGameDeposits';
import FlaggedWithdrawalsManagement from '../components/pages/flaggedWithdrawals';
import DomainsManagement from '../components/pages/domains';
import ForbiddenPage from '../components/pages/Forbidden';
import LoginPage from '../components/auth/LoginPage';
import ForgotPasswordContactPage from '../components/auth/ForgotPasswordContactPage';
import ForgotPasswordOtpPage from '../components/auth/ForgotPasswordOtpPage';
import ForgotPasswordResetPage from '../components/auth/ForgotPasswordResetPage';
import AgentManagement from '../components/pages/agents';
import SuperAgentManagement from '../components/pages/superAgents';
import SuperAgentDetailPage from '../components/pages/superAgents/SuperAgentDetailPage';
import AgentDetailPage from '../components/pages/agents/AgentDetailPage';
import AgentPaymentTransactionsPage from '../components/pages/agents/AgentPaymentTransactionsPage';
import AgentWalletTransferPage from '../components/pages/agents/AgentWalletTransferPage';
import ShopDetailPage from '../components/pages/agents/ShopDetailPage';
import ShopManagement from '../components/pages/shops';
import BroadcastManagement from '../components/pages/broadcasts/BroadcastManagement';
import InAppPopupManagement from '../components/pages/in-app-popups/InAppPopupManagement';
import NotificationsPage from '../components/pages/notifications/NotificationsPage';
import CashierManagement from '../components/pages/cashiers';
import IntegrationLogsManagement from '../components/pages/integrationLogs';
import AuditLogsPage from '../components/pages/auditLogs';
import { Permission } from './permissions';

type LayoutType = 'main' | 'none';

export interface RouteMeta {
  title: string;
  icon?: LucideIcon;
  nav?: boolean;
}

export interface AppRouteConfig {
  path: string;
  element: ComponentType;
  permission?: Permission;
  /** If true, only super_admin and system_admin (see ProtectedRoute requiredRole). */
  superAdminOnly?: boolean;
  layout?: LayoutType;
  meta?: RouteMeta;
}

export const PUBLIC_ROUTES: AppRouteConfig[] = [
  {
    path: '/login',
    element: LoginPage,
    layout: 'none',
    meta: {
      title: 'Login',
    },
  },
  {
    path: '/forgot-password',
    element: ForgotPasswordContactPage,
    layout: 'none',
    meta: { title: 'Forgot Password' },
  },
  {
    path: '/forgot-password/otp',
    element: ForgotPasswordOtpPage,
    layout: 'none',
    meta: { title: 'Forgot Password — OTP' },
  },
  {
    path: '/forgot-password/reset',
    element: ForgotPasswordResetPage,
    layout: 'none',
    meta: { title: 'Forgot Password — Reset' },
  },
];

export const PROTECTED_ROUTES: AppRouteConfig[] = [
  {
    path: '/dashboard',
    element: Dashboard,
    layout: 'main',
    permission: 'dashboard',
    meta: {
      title: 'Dashboard',
      icon: LayoutDashboard,
      nav: true,
    },
  },
  {
    path: '/users',
    element: UsersManagement,
    layout: 'main',
    permission: 'userManagement',
    meta: {
      title: 'Players',
      icon: Users,
      nav: false,
    },
  },
  {
    path: '/users/:id',
    element: UserDetailPage,
    layout: 'main',
    permission: 'userManagement',
    meta: {
      title: 'Player details',
    },
  },
  {
    path: '/cashiers',
    element: CashierManagement,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Cashier Management',
      icon: Users,
      nav: false,
    },
  },
  {
    path: '/transactions/:id',
    element: TransactionDetailPage,
    layout: 'main',
    permission: 'transactionManagement',
    meta: {
      title: 'Transaction details',
      nav: false,
    },
  },
  {
    path: '/transactions',
    element: TransactionsManagement,
    layout: 'main',
    permission: 'transactionManagement',
    meta: {
      title: 'Payment transactions',
      icon: CreditCard,
      nav: true,
    },
  },
  {
    path: '/withdrawal-requests',
    element: WithdrawalManagement,
    layout: 'main',
    permission: 'withdrawalManagement',
    meta: {
      title: 'Withdrawal Requests',
      icon: ArrowUpRight,
      nav: true,
    },
  },
  {
    path: '/flagged-withdrawals',
    element: FlaggedWithdrawalsManagement,
    layout: 'main',
    permission: 'withdrawalManagement',
    meta: {
      title: 'Flagged Withdrawals',
      icon: ShieldAlert,
      nav: true,
    },
  },
  {
    path: '/manual-game-deposits',
    element: ManualGameDepositsManagement,
    layout: 'main',
    permission: 'manualGameDepositManagement',
    meta: {
      title: 'Manual Game Deposits',
      icon: Coins,
      nav: true,
    },
  },
  {
    path: '/wallets',
    element: GameWalletsManagement,
    layout: 'main',
    permission: 'walletManagement',
    meta: {
      title: 'Game Wallets',
      icon: Wallet,
      nav: false,
    },
  },
  {
    path: '/wallets/:providerId',
    element: GameWalletProviderDetail,
    layout: 'main',
    permission: 'walletManagement',
    meta: {
      title: 'Provider Wallet Detail',
      nav: false,
    },
  },
  {
    path: '/tickets',
    element: TicketManagement,
    layout: 'main',
    permission: 'ticketManagement',
    meta: {
      title: 'Support Tickets',
      icon: MessageSquare,
      nav: true,
    },
  },
  {
    path: '/tickets/:ticketId',
    element: TicketManagement,
    layout: 'main',
    permission: 'ticketManagement',
    meta: {
      title: 'Ticket Details',
      nav: false,
    },
  },
  {
    path: '/admins',
    element: AdminManagement,
    layout: 'main',
    permission: 'adminManagement',
    meta: {
      title: 'Admin Management',
      icon: Shield,
      nav: false,
    },
  },
  {
    path: '/super-agents',
    element: SuperAgentManagement,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Super Agent Management',
      icon: ShieldCheck,
      nav: false,
    },
  },
  {
    path: '/super-agents/:id',
    element: SuperAgentDetailPage,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Super agent details',
    },
  },
  {
    path: '/agents',
    element: AgentManagement,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Agent Management',
      icon: UserCog,
      nav: false,
    },
  },
  {
    path: '/shops',
    element: ShopManagement,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Shop Management',
      icon: Store,
      nav: false,
    },
  },
  {
    path: '/shops/:shopId',
    element: ShopDetailPage,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Shop details',
    },
  },
  {
    path: '/agents/:id/transactions',
    element: AgentPaymentTransactionsPage,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Agent payment transactions',
      nav: false,
    },
  },
  {
    path: '/agents/:id/wallet-transfer',
    element: AgentWalletTransferPage,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Agent wallet transfer',
      nav: false,
    },
  },
  {
    path: '/agents/:id',
    element: AgentDetailPage,
    layout: 'main',
    permission: 'agentManagement',
    meta: {
      title: 'Agent details',
    },
  },
  {
    path: '/game-providers',
    element: GameProvidersManagement,
    layout: 'main',
    permission: 'gameProviderManagement',
    meta: {
      title: 'Game Providers',
      icon: Building2,
      nav: false,
    },
  },
  {
    path: '/vfx-config',
    element: ScheduleManagement,
    layout: 'main',
    permission: 'vfxConfigManagement',
    meta: {
      title: 'VFX & Background Config',
      icon: Sparkles,
      nav: false,
    },
  },
  {
    path: '/vfx-config/schedules/:id',
    element: ScheduleDetail,
    layout: 'main',
    permission: 'vfxConfigManagement',
    meta: {
      title: 'Schedule Details',
      nav: false,
    },
  },
  {
    path: '/transaction-limits',
    element: TransactionLimitsManagement,
    layout: 'main',
    permission: 'transactionLimitsManagement',
    meta: {
      title: 'Transaction Limits & Fraud Detection',
      icon: AlertTriangle,
      nav: false,
    },
  },
  {
    path: '/messaging-settings',
    element: MessagingSettingsManagement,
    layout: 'main',
    permission: 'transactionLimitsManagement',
    meta: {
      title: 'Messaging pricing',
      icon: MessageSquare,
      nav: false,
    },
  },
  {
    path: '/signup-bonus',
    element: SignupBonusManagement,
    layout: 'main',
    permission: 'transactionLimitsManagement',
    meta: {
      title: 'Welcome Bonus',
      icon: Gift,
      nav: false,
    },
  },
  {
    path: '/provider-fees',
    element: ProviderFeesManagement,
    layout: 'main',
    permission: 'transactionLimitsManagement',
    meta: {
      title: 'Fee Configuration',
      icon: Percent,
      nav: false,
    },
  },
  {
    path: '/usd-wallets',
    element: UsdWalletsManagement,
    layout: 'main',
    // KIOSK-720: View Wallets follows walletManagement (matches backend gate; support_admin excluded).
    permission: 'walletManagement',
    meta: {
      title: 'USD Wallets',
      icon: DollarSign,
      nav: false,
    },
  },
  {
    path: '/credit-wallets',
    element: CreditWalletsManagement,
    layout: 'main',
    // KIOSK-720: View Wallets follows walletManagement (matches backend gate; support_admin excluded).
    permission: 'walletManagement',
    meta: {
      title: 'Credit Wallets',
      icon: WalletCards,
      nav: false,
    },
  },
  {
    path: '/cash-transactions',
    element: CashTransactionsManagement,
    layout: 'main',
    // KIOSK-720: View Cash Settlement = system/super/finance/operation only (platform/support = No).
    permission: 'cashSettlementManagement',
    meta: {
      title: 'Cash Transactions',
      icon: Banknote,
      nav: false,
    },
  },
  {
    path: '/cash-transactions/:id',
    element: CashTransactionDetailPage,
    layout: 'main',
    permission: 'cashSettlementManagement',
    meta: {
      title: 'Cash Transaction',
      nav: false,
    },
  },
  {
    path: '/domains',
    element: DomainsManagement,
    layout: 'main',
    permission: 'domainManagement',
    meta: {
      title: 'Domain Management',
      icon: Globe,
      nav: false,
    },
  },
  {
    path: '/integration-logs',
    element: IntegrationLogsManagement,
    layout: 'main',
    superAdminOnly: true,
    meta: {
      title: 'API Logs',
      icon: Terminal,
      nav: true,
    },
  },
  {
    path: '/audit-logs',
    element: AuditLogsPage,
    layout: 'main',
    superAdminOnly: true,
    meta: {
      title: 'Audit log',
      icon: ClipboardList,
      nav: true,
    },
  },
  {
    path: '/notifications',
    element: NotificationsPage,
    layout: 'main',
    meta: {
      title: 'Notification center',
      icon: Bell,
    },
  },
  {
    path: '/broadcasts',
    element: BroadcastManagement,
    layout: 'main',
    superAdminOnly: true,
    meta: {
      title: 'Global broadcasts',
      icon: Megaphone,
      nav: true,
    },
  },
  {
    path: '/in-app-popups',
    element: InAppPopupManagement,
    layout: 'main',
    superAdminOnly: true,
    meta: {
      title: 'In-app popups',
      icon: LayoutPanelTop,
      nav: true,
    },
  },
  {
    path: '/forbidden',
    element: ForbiddenPage,
    layout: 'main',
    meta: {
      title: 'Access Denied',
    },
  },
];

/** Paths under the collapsible “Wallets” sidebar group (Game / USD / Credit). */
export const WALLET_NAVIGATION_PATHS = ['/wallets', '/usd-wallets', '/credit-wallets'] as const;

/** Paths under collapsible “Cash Settlement” sidebar group */
export const CASH_SETTLEMENT_NAVIGATION_PATHS = ['/cash-transactions'] as const;

export function getWalletNavRoutes(
  hasPermission: (permission?: Permission) => boolean,
): AppRouteConfig[] {
  const byPath = new Map<string, AppRouteConfig>();
  for (const r of PROTECTED_ROUTES) {
    byPath.set(r.path, r);
  }
  return WALLET_NAVIGATION_PATHS.map((p) => byPath.get(p)).filter(
    (r): r is AppRouteConfig => !!r && (!r.permission || hasPermission(r.permission)),
  );
}

export function getCashSettlementNavRoutes(
  hasPermission: (permission?: Permission) => boolean,
): AppRouteConfig[] {
  const byPath = new Map<string, AppRouteConfig>();
  for (const r of PROTECTED_ROUTES) {
    byPath.set(r.path, r);
  }
  return CASH_SETTLEMENT_NAVIGATION_PATHS.map((p) => byPath.get(p)).filter(
    (r): r is AppRouteConfig => !!r && (!r.permission || hasPermission(r.permission)),
  );
}

/** Top-level paths shown under the collapsible “Account management” sidebar group */
export const ACCOUNT_MANAGEMENT_PATHS = ['/admins', '/super-agents', '/agents', '/shops', '/cashiers', '/users'] as const;

export function getAccountManagementNavRoutes(
  hasPermission: (permission?: Permission) => boolean,
): AppRouteConfig[] {
  const byPath = new Map<string, AppRouteConfig>();
  for (const r of PROTECTED_ROUTES) {
    byPath.set(r.path, r);
  }
  return ACCOUNT_MANAGEMENT_PATHS.map((p) => byPath.get(p)).filter(
    (r): r is AppRouteConfig => !!r && (!r.permission || hasPermission(r.permission)),
  );
}

/** Top-level paths under collapsible “Platform configuration” in the sidebar */
export const PLATFORM_CONFIGURATION_PATHS = [
  '/domains',
  '/vfx-config',
  '/game-providers',
  '/transaction-limits',
  '/messaging-settings',
  '/signup-bonus',
  '/provider-fees',
  '/integration-logs',
] as const;

export function getPlatformConfigurationNavRoutes(
  hasPermission: (permission?: Permission) => boolean,
  isElevated: boolean,
): AppRouteConfig[] {
  const byPath = new Map<string, AppRouteConfig>();
  for (const r of PROTECTED_ROUTES) {
    byPath.set(r.path, r);
  }
  return PLATFORM_CONFIGURATION_PATHS.map((p) => byPath.get(p)).filter(
    (r): r is AppRouteConfig =>
      !!r &&
      (!r.superAdminOnly || isElevated) &&
      (!r.permission || hasPermission(r.permission)),
  );
}

/** Top-level paths shown under the collapsible “Notifications” sidebar group */
export const NOTIFICATION_PATHS = ['/notifications', '/broadcasts', '/in-app-popups'] as const;

export function getNotificationNavRoutes(
  hasPermission: (permission?: Permission) => boolean,
  isElevated: boolean,
): AppRouteConfig[] {
  const byPath = new Map<string, AppRouteConfig>();
  for (const r of PROTECTED_ROUTES) {
    byPath.set(r.path, r);
  }
  return NOTIFICATION_PATHS.map((p) => byPath.get(p)).filter(
    (r): r is AppRouteConfig =>
      !!r &&
      (!r.superAdminOnly || isElevated) &&
      (!r.permission || hasPermission(r.permission)),
  );
}

export const DEFAULT_REDIRECT = '/dashboard';

// Helper function to get the first accessible route for a user
export const getFirstAccessibleRoute = (
  hasPermission: (permission?: Permission) => boolean,
  isSuperAdmin: boolean = false
): string => {
  // Super admin can always access dashboard
  if (isSuperAdmin) {
    return '/dashboard';
  }

  // Find first route that user has permission for
  for (const route of PROTECTED_ROUTES) {
    // Skip dashboard for non-super-admin
    if (route.path === '/dashboard') {
      continue;
    }

    // If route has no permission requirement or user has permission
    if (!route.permission || hasPermission(route.permission)) {
      return route.path;
    }
  }

  // Fallback to forbidden if no accessible route found
  return '/forbidden';
};

